import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TextButton } from "../buttons/TextButton";
import { LoadingSpinner } from "../loading/LoadingSpinner";
import { Small } from "../../styles/stringStyles";
import { useStyles } from "../../styles/theme";
import { hexWithOpacity } from "../../utils/hexWithOpacity";

type Props = {
  setCode: (code: string) => void;
  code: string;
  error: string | undefined;
  handleResendCode: () => void;
  loading?: boolean;
  style?: CSSProperties;
  resendLabel?: string;
  resendDisabled?: boolean;
  resendHidden?: boolean;
  /** "off" where the code does not come by SMS: the keyboard would offer the device's latest SMS code. */
  autoComplete?: "one-time-code" | "off";
  /** Further actions, centered below the resend button. */
  children?: React.ReactNode;
};

export const CodeInputField = ({
  setCode,
  code,
  error,
  handleResendCode,
  loading,
  style,
  resendLabel,
  resendDisabled,
  resendHidden,
  autoComplete = "one-time-code",
  children,
}: Props) => {
  const { t } = useTranslation();
  const theme = useStyles();

  const textRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    // Focus the input when component mounts
    textRef.current?.focus();
  }, []);

  useEffect(() => {
    if (error) {
      setCode("");
    }
  }, [error, setCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Only allow numeric input
    value = value.replace(/[^0-9]/g, "");

    let newValue = value;
    if (value.length > 6) {
      newValue = value.slice(0, 6);
    }
    setCode(newValue);
  };

  const handlePress = () => {
    textRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <Container style={style}>
      <InnerContainer onClick={handlePress}>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        ) : (
          [...Array(6)].map((_, index) => {
            const isActive =
              isFocused &&
              (code[index - 1] !== undefined || index === 0) &&
              (code[index] === undefined || code[index] === "");

            return (
              <ItemContainer key={"code" + index} $isActive={isActive}>
                <CodeText>{code[index]}</CodeText>
              </ItemContainer>
            );
          })
        )}
        <HiddenInput
          ref={textRef}
          value={code}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={autoComplete}
          name={autoComplete === "one-time-code" ? "one-time-code" : undefined}
          autoFocus={true}
          maxLength={6}
        />
      </InnerContainer>
      <ErrorText>{error ?? " "}</ErrorText>
      {!resendHidden && (
        <TextButton
          text={resendLabel ?? t("APPOINTMENT_PHONE_NUMBER_RESEND_CODE")}
          noUnderline={false}
          color={theme.colors.dark}
          onClick={handleResendCode}
          loading={loading}
          disabled={resendDisabled}
        />
      )}
      {children}
    </Container>
  );
};

const Container = styled.div`
  padding: 0 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 5px;
  position: relative;
  cursor: text;
`;

const CodeText = styled.span`
  font-size: 20px;
  font-family: "Ubuntu", sans-serif;
  font-weight: bold;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  cursor: text;
`;

const ItemContainer = styled.div<{ $isActive: boolean }>`
  flex: 1;
  max-width: 50px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  background-color: ${({ theme, $isActive }) =>
    $isActive ? hexWithOpacity(theme.colors.primary, 0.2) : theme.colors.light};
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ErrorText = styled(Small)`
  color: ${({ theme }) => theme.colors.lightRed};
`;
